import React, { useEffect, useMemo, useState } from "react";
import { Download, FileSpreadsheet, Loader2, Upload, X } from "lucide-react";
import toast from "react-hot-toast";

import {
  DownloadBiodataNonUserExport,
  GetBiodataNonUserExportStatus,
} from "./apis/get";
import { PostBiodataNonUserExport } from "./apis/post";

type JobData = {
  job_id: number;
  job_status: string;
  error?: string;
  download_ready?: boolean;
  result?: {
    files_received?: number;
    files_extracted?: number;
    new_records_count?: number;
    duplicate_upload_count?: number;
    failed_count?: number;
    ignored_count?: number;
  non_biodata_count?: number;
  no_mobile_count?: number;
    existing_user_count?: number;
    excel_user_count?: number;
    cumulative_excel_count?: number;
    duplicates?: Array<Record<string, string>>;
    file_results?: Array<Record<string, string>>;
  };
};

export default function BiodataNonUserExport() {
  const [files, setFiles] = useState<File[]>([]);
  const [job, setJob] = useState<JobData | null>(null);
  const [uploading, setUploading] = useState(false);
  const processing = job?.job_status === "pending" || job?.job_status === "processing";

  useEffect(() => {
    if (!job?.job_id || !processing) return;
    const timer = window.setInterval(async () => {
      try {
        const response = await GetBiodataNonUserExportStatus(job.job_id);
        setJob(response?.data || null);
      } catch (error) {
        console.error("Failed to poll biodata export", error);
      }
    }, 3000);
    return () => window.clearInterval(timer);
  }, [job?.job_id, processing]);

  const totalSize = useMemo(
    () => files.reduce((sum, file) => sum + file.size, 0),
    [files],
  );

  const addFiles = (incoming: FileList | null) => {
    if (!incoming) return;
    const next = [...files, ...Array.from(incoming)].slice(0, 25);
    setFiles(next);
  };

  const start = async () => {
    if (!files.length) return toast.error("Select at least one biodata");
    setUploading(true);
    try {
      const form = new FormData();
      files.forEach((file) => form.append("files", file));
      const response = await PostBiodataNonUserExport(form);
      setJob(response?.data || null);
      toast.success("Extraction started");
    } catch (error: any) {
      toast.error(error?.message || "Unable to start extraction");
    } finally {
      setUploading(false);
    }
  };

  const result = job?.result || {};
  const cards = [
    ["Received", result.files_received || files.length, "text-blue-300"],
    ["New Files", result.new_records_count || 0, "text-cyan-300"],
    ["Duplicate Skipped", result.duplicate_upload_count || 0, "text-violet-300"],
    ["Oversized Ignored", result.ignored_count || 0, "text-orange-300"],
    ["Non-Biodata Ignored", result.non_biodata_count || 0, "text-slate-300"],
    ["No Mobile Ignored", result.no_mobile_count || 0, "text-amber-300"],
    ["Already Exists", result.existing_user_count || 0, "text-amber-300"],
    ["Added This Upload", result.excel_user_count || 0, "text-emerald-300"],
    ["Total Excel Rows", result.cumulative_excel_count || 0, "text-emerald-200"],
    ["Failed", result.failed_count || 0, "text-rose-300"],
  ];

  return (
    <div className="min-h-screen bg-slate-950 p-6 text-slate-100">
      <div className="mx-auto max-w-6xl space-y-5">
        <header className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-400">
            <FileSpreadsheet size={16} /> Biodata Operations
          </div>
          <h1 className="mt-2 text-2xl font-bold">New User Biodata Excel</h1>
          <p className="mt-2 text-sm text-slate-400">
            Upload 1–25 PDF or image biodatas. Existing profiles are removed using Name + DOB, Name + Father, or Name + Mother.
          </p>
        </header>

        <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
          <label className="flex min-h-40 cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-700 bg-slate-950/70 p-6 text-center transition hover:border-blue-500">
            <Upload className="text-blue-400" />
            <span className="mt-3 text-sm font-semibold">Choose single or bulk biodata</span>
            <span className="mt-1 text-xs text-slate-500">PDF, JPG, JPEG, PNG or WEBP · 20 MB each · 100 MB combined</span>
            <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={(event) => addFiles(event.target.files)} />
          </label>

          {files.length > 0 && (
            <div className="mt-4 space-y-2">
              <div className="flex justify-between text-xs text-slate-500"><span>{files.length} files</span><span>{(totalSize / 1024 / 1024).toFixed(2)} MB</span></div>
              <div className="max-h-52 space-y-2 overflow-y-auto">
                {files.map((file, index) => (
                  <div key={`${file.name}-${index}`} className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-950 px-3 py-2 text-sm">
                    <span className="truncate">{file.name}</span>
                    <button type="button" disabled={processing} onClick={() => setFiles((current) => current.filter((_, itemIndex) => itemIndex !== index))} className="text-slate-500 hover:text-rose-400"><X size={15} /></button>
                  </div>
                ))}
              </div>
              <button type="button" disabled={uploading || processing} onClick={start} className="mt-3 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-bold text-white disabled:opacity-50">
                {(uploading || processing) && <Loader2 size={16} className="animate-spin" />}
                {processing ? "Processing biodatas" : "Extract and check users"}
              </button>
            </div>
          )}
        </section>

        {job && (
          <section className="space-y-4 rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div><div className="text-xs uppercase tracking-widest text-slate-500">Job #{job.job_id}</div><div className="mt-1 font-semibold capitalize">{job.job_status}</div></div>
              {job.download_ready && (
                <button type="button" onClick={() => DownloadBiodataNonUserExport(job.job_id)} className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-bold text-white"><Download size={16} /> Download Excel</button>
              )}
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {cards.map(([label, value, tone]) => <div key={String(label)} className="rounded-xl border border-slate-800 bg-slate-950 p-4"><div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div><div className={`mt-2 text-2xl font-bold ${tone}`}>{value}</div></div>)}
            </div>
            {job.error && <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-200">{job.error}</div>}
            {!!result.file_results?.length && (
              <div className="overflow-hidden rounded-xl border border-slate-800">
                <div className="border-b border-slate-800 bg-slate-950 px-4 py-3 text-xs font-bold uppercase tracking-widest text-slate-500">File results</div>
                <div className="max-h-72 divide-y divide-slate-800 overflow-y-auto">
                  {result.file_results.map((item, index) => <div key={index} className="flex items-start justify-between gap-4 px-4 py-3 text-sm"><span className="break-all">{item.filename}</span><span className={item.status === "failed" ? "text-rose-300" : item.status === "ignored_size_limit" ? "text-orange-300" : item.status === "ignored_no_mobile" ? "text-amber-300" : item.status === "ignored_non_biodata" ? "text-slate-300" : item.status === "duplicate_skipped" ? "text-violet-300" : "text-emerald-300"}>{item.status}{item.error ? ` · ${item.error}` : ""}</span></div>)}
                </div>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
